<template>
  <div class="min-h-full p-4 pt-6 md:pt-12 bg-transparent text-slate-900 dark:text-[#F1F5F9]">
    <div class="max-w-5xl mx-auto space-y-8 md:space-y-12">
      <!-- Header Section -->
      <div class="text-center space-y-3">
        <div
          class="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gradient-to-tr from-indigo-600 to-violet-600 rounded-2xl shadow-lg shadow-indigo-500/20 text-white"
        >
          <svg
            class="w-7 h-7 md:w-8 md:h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <h1
          class="text-2xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight"
        >
          Welcome to Educanium
        </h1>
        <p
          v-if="!allStepsCompleted"
          class="text-xs sm:text-sm md:text-base text-slate-500 dark:text-slate-400 max-w-xl mx-auto"
        >
          Complete the foundational setup in <strong class="text-slate-900 dark:text-white font-bold">3 simple steps</strong> to activate all modules.
        </p>
      </div>

      <!-- Progress Steps - Mobile & Desktop -->
      <div v-if="!allStepsCompleted">
        <!-- Mobile: Vertical Layout -->
        <div class="block md:hidden space-y-3">
          <div
            v-for="step in steps"
            :key="step.number"
            class="flex items-center p-3.5 rounded-2xl transition-all duration-200"
            :class="
              step.number === currentStepNumber
                ? 'bg-white dark:bg-[#111C30] shadow-sm border-2 border-indigo-500/70'
                : step.completed
                ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/40'
                : 'bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449]'
            "
          >
            <div
              class="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm transition-all duration-200 shrink-0"
              :class="getStepClass(step.number)"
            >
              <span v-if="!step.completed">{{ step.number }}</span>
              <svg
                v-else
                class="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3.5 flex-1 min-w-0">
              <p class="font-bold text-slate-900 dark:text-white text-sm truncate">
                {{ step.title }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ step.description }}</p>
            </div>
          </div>
        </div>

        <!-- Desktop: Horizontal Layout -->
        <div class="hidden md:flex items-center justify-between bg-white dark:bg-[#111C30] p-6 rounded-3xl border border-slate-200/90 dark:border-[#263449] shadow-xs">
          <div
            v-for="step in steps"
            :key="step.number"
            class="flex items-center"
            :class="{ 'flex-1': step.number < 3 }"
          >
            <div class="flex items-center">
              <div
                class="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm transition-all duration-200 shadow-sm shrink-0"
                :class="getStepClass(step.number)"
              >
                <span v-if="!step.completed">{{ step.number }}</span>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3 text-left">
                <p class="font-bold text-slate-900 dark:text-white text-sm">
                  {{ step.title }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">{{ step.description }}</p>
              </div>
            </div>
            <div
              v-if="step.number < 3"
              class="flex-1 h-1.5 mx-6 rounded-full transition-all duration-300"
              :class="
                step.completed
                  ? 'bg-emerald-500'
                  : 'bg-slate-200 dark:bg-[#1E293B]'
              "
            ></div>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div
        v-if="!allStepsCompleted"
        class="bg-white dark:bg-[#111C30] rounded-3xl p-6 sm:p-10 border border-slate-200/90 dark:border-[#263449] shadow-xs text-center"
      >
        <div class="max-w-2xl mx-auto space-y-4">
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40"
          >
            Step {{ currentStep.number }} of 3
          </span>

          <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            {{ currentStep.title }}
          </h2>

          <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            {{ currentStep.longDescription }}
          </p>

          <div class="pt-4 flex justify-center">
            <button
              @click="navigateToStep"
              class="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-98 transition-all shadow-md shadow-indigo-600/20 text-sm"
              :disabled="loading"
            >
              <span
                v-if="!loading"
                class="flex items-center justify-center gap-1.5"
              >
                <span>{{ currentStep.buttonText }}</span>
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
              <span
                v-else
                class="flex items-center justify-center gap-2"
              >
                <AppLoader variant="button" class="w-4 h-4 text-white" />
                <span>Processing...</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Success Animation & Completion Card -->
      <div
        v-if="allStepsCompleted"
        class="relative overflow-hidden bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-3xl p-8 sm:p-12 text-center shadow-xl max-w-xl mx-auto font-sans"
      >
        <!-- Polished Success Icon -->
        <div class="relative mb-6">
          <div
            class="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-3xl flex items-center justify-center mx-auto shadow-sm ring-8 ring-emerald-50/60 dark:ring-emerald-950/30"
          >
            <svg
              class="w-8 h-8 sm:w-10 sm:h-10"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
          </div>
        </div>

        <!-- Text Content -->
        <div class="relative z-10 space-y-3">
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold uppercase tracking-wider"
          >
            <span
              class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
            ></span>
            Setup Completed
          </span>

          <h2
            class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white"
          >
            You're Ready to Go!
          </h2>

          <p
            class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed"
          >
            Your system configuration is complete. You can now start managing students, teachers, and smart terminals seamlessly.
          </p>

          <!-- Action Button -->
          <div class="pt-4">
            <button
              @click="goToDashboard"
              class="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white font-semibold text-sm rounded-xl shadow-md shadow-indigo-600/20 transition-all duration-200"
            >
              <span>Go to Dashboard</span>
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from "vuex";

  export default {
    name: "Onboarding",
    data() {
      return {
        loading: false,
        steps: [
          {
            number: 1, // Signup
            title: "Signup",
            description: "Your account is ready",
            longDescription:
              "You have successfully signed up and can now set up your school.",
            buttonText: "Next",
            route: null, // no route needed
            completed: true, // always complete
          },
          {
            number: 2, // Create School
            title: "Create School",
            description: "Set up your school profile",
            longDescription:
              "Start by creating your school profile with basic information like name, address, and contact details. This will be the foundation of your education management system.",
            buttonText: "Create School",
            route: { name: "createSchool" },
            completed: false,
          },
          {
            number: 3, // Add Classes
            title: "Add Classes",
            description: "Create your first class",
            longDescription:
              "Set up classes for your school. You can organize students by grade, section, or any other criteria that works for your institution.",
            buttonText: "Create Classes",
            route: { name: "Class" },
            completed: false,
          },
        ],
        hasFetchedClasses: false,
      };
    },
    computed: {
      ...mapState(["user", "classes"]),

      validClasses() {
        return (this.classes || []).filter(
          (cls) => cls.name.toLowerCase() !== "passout",
        );
      },

      currentStepNumber() {
        // Step 1 is always complete (Signup)
        if (!this.user?.schoolId) return 2;
        if (this.validClasses.length === 0) return 3;
        return 4; // all steps completed
      },

      currentStep() {
        if (this.currentStepNumber <= 3) {
          return this.steps[this.currentStepNumber - 1]; // array index = stepNumber - 1
        }
        return this.steps[2]; // last step
      },

      allStepsCompleted() {
        return this.currentStepNumber > 3;
      },
    },
    watch: {
      "user.schoolId": {
        async handler(newVal) {
          if (newVal && !this.hasFetchedClasses) {
            await this.fetchAllClasses();
            this.hasFetchedClasses = true;
          }
        },
        immediate: true,
      },
    },
    async beforeMount() {
      await this.$store.dispatch("initializeApp");
      this.updateStepCompletion();
    },
    methods: {
      ...mapActions(["fetchAllClasses"]),

      updateStepCompletion() {
        this.steps[1].completed = !!this.user?.schoolId;
        this.steps[2].completed = this.validClasses.length > 0;
      },

      getStepClass(stepNumber) {
        const step = this.steps[stepNumber - 1]; // adjust for 1-based numbering
        if (step.completed) {
          return "bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg";
        } else if (step.number === this.currentStepNumber) {
          return "bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg ring-4 ring-indigo-200";
        } else {
          return "bg-gray-300";
        }
      },

      navigateToStep() {
        const step = this.currentStep;
        if (!step.route) return; // Skip signup step
        this.loading = true;
        setTimeout(() => {
          this.$router.replace(step.route);
          this.loading = false;
        }, 500);
      },

      goToDashboard() {
        this.$store.commit("SET_ONBOARDING_COMPLETED", true);
        this.$router.replace("/");
      },
    },
  };
</script>

<style scoped>
  /* Additional custom styles for animations */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out;
  }

  @keyframes bounce-slow {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  .animate-bounce-slow {
    animation: bounce-slow 2s infinite;
  }
</style>
